import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf } from '@truecare/api-client';
import { Alert, Badge, type BadgeTone, Card, CardHeader, DateInput, StatusBadge, useFocusTrap } from '@truecare/ui';
import type {
  BuyerShiftListItem,
  BuyerShiftRequestListItem,
  BuyerVisibleCaregiverProfile,
  FacilityCapability,
} from '@truecare/schemas';
import { DEFAULT_BREAK_MINUTES, DEFAULT_BREAK_THRESHOLD_HOURS, defaultBreakMinutes } from '@truecare/schemas';
import { api } from '../api';
import {
  emptyShiftListMessage,
  Pager,
  SHIFT_LIST_PAGE_SIZE,
  ShiftListControls,
  ShiftStatusBadge,
  usePageClamp,
  useShiftListState,
} from './ShiftListControls';

type Persona = 'FACILITY' | 'HOMECARE';
type Tab = 'requests' | 'shifts' | 'post' | 'schedules';

interface Recipient {
  id: string;
  name: string;
  /** False when the recipient's address hasn't been geocoded yet — such a
   *  recipient can't be matched, so the picker blocks selecting one. */
  geocoded: boolean;
}

/**
 * Phase 9e — buyer portal panel. Used by both Facility and Homecare
 * branches of /me. `persona` switches the endpoint URLs + the
 * recipient picker on Post-a-shift (homecare only).
 *
 * Tabs (facility): Open shifts / Booked shifts / Post a shift / Create a
 * schedule (homecare: Open visits / Booked visits / Book a visit). Each tab is
 * its own TanStack query; mutations invalidate the relevant query. "Booked shifts"
 * (3.4) is a single time-sorted list of every booked shift, with a status
 * filter + sort direction + server-side pagination.
 *
 * Intentionally minimal styling — Phase 14 + 15 own the polish pass.
 */
export function BuyerShiftPanel({
  persona,
  recipients,
  showTitle = true,
  canPost = true,
  postPausedMessage,
  capabilities,
}: {
  persona: Persona;
  recipients?: Recipient[];
  /** Show the "Shifts" card title. False when embedded in a workspace tab that
   *  already names the section (facility /me); true for the stacked homecare
   *  layout where the panel needs its own section heading. */
  showTitle?: boolean;
  /** False when the buyer account is not ACTIVE (suspended/prospect/terminated).
   *  The backend rejects posting from a non-ACTIVE buyer; this hides the form as
   *  client-side defense in depth so the affordance matches the paused banner. */
  canPost?: boolean;
  /** Phase 23 (23-6) — overrides the generic "not active" copy in the paused
   *  Post tab when posting is blocked for a specific reason OTHER than account
   *  status (e.g. an unsigned Client Agreement). Only meaningful when
   *  canPost is false. */
  postPausedMessage?: string;
  /** RBAC 3.2 — the facility login's capability set. When present, the create
   *  tabs (post / schedules) are hidden without SHIFT_POST. Undefined for
   *  homecare (a single-user buyer with no roles — always full). */
  capabilities?: FacilityCapability[];
}) {
  const [tab, setTab] = useState<Tab>('requests');
  // Audit #11 — after a successful post the form unmounts (we switch to the
  // requests tab), so success feedback lands there as a dismissible banner.
  const [justPosted, setJustPosted] = useState(false);
  // undefined caps (homecare) ⇒ allowed; a facility login must hold SHIFT_POST.
  const canCreate = capabilities ? capabilities.includes('SHIFT_POST') : true;
  // Audit #20 — viewing (requests / shifts / schedules) is SHIFT_VIEW; creating
  // is SHIFT_POST. Approver holds SHIFT_VIEW but not SHIFT_POST, so it must be
  // able to VIEW recurring schedules (previously the whole tab was hidden
  // behind SHIFT_POST). Payer has neither.
  const canView = capabilities ? capabilities.includes('SHIFT_VIEW') : true;
  // Labels are sentence case (matching every other portal tab) and parallel
  // across the two buyer personas: facility talks in "shifts", homecare in
  // "visits". View tabs first (open / booked), then the create actions.
  const subTabs: { value: Tab; label: string }[] = [
    { value: 'requests', label: persona === 'HOMECARE' ? 'Open visits' : 'Open shifts' },
    { value: 'shifts', label: persona === 'HOMECARE' ? 'Booked visits' : 'Booked shifts' },
    // RBAC 3.2 — a Coordinator has SHIFT_POST; an Approver/Payer does not, so
    // they see the view tabs but not the create actions.
    ...(canCreate
      ? [{ value: 'post' as Tab, label: persona === 'HOMECARE' ? 'Book a visit' : 'Post a shift' }]
      : []),
    // Phase 21 (3.1-5) — recurring schedules are facility-only in v1 (homecare
    // stays per-recipient ad-hoc). The backend has no homecare schedule endpoint,
    // so the tab is hidden rather than 404-ing. Audit #20 — gated on SHIFT_VIEW
    // (viewing), not SHIFT_POST: create + cancel inside are gated separately.
    ...(persona === 'FACILITY' && canView
      ? [{ value: 'schedules' as Tab, label: 'Schedules' }]
      : []),
  ];
  return (
    <Card padding="lg" className={showTitle ? 'mt-8' : ''}>
      {showTitle && <CardHeader title="Shifts" />}
      {/* Sub-navigation within Shifts. Responsive like <PersonaWorkspace> (audit
          M4 — Option B): a <select> on mobile so it doesn't stack a wrapping
          pill row under the workspace's own mobile tab select, and the pill row
          on sm+. */}
      <div className="mb-4">
        <label className="sr-only" htmlFor="buyer-shift-subtab">
          Shift view
        </label>
        <select
          id="buyer-shift-subtab"
          className="w-full rounded-md border border-ink-soft bg-paper px-3 py-2 font-body text-sm text-ink sm:hidden"
          value={tab}
          onChange={(e) => setTab(e.target.value as Tab)}
        >
          {subTabs.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <div className="hidden gap-1 -mx-2 border-b border-ink-soft sm:flex" role="tablist">
          {subTabs.map((t) => (
            <TabButton key={t.value} active={tab === t.value} onClick={() => setTab(t.value)}>
              {t.label}
            </TabButton>
          ))}
        </div>
      </div>
      {tab === 'requests' && (
        <RequestsTab
          persona={persona}
          capabilities={capabilities}
          justPosted={justPosted}
          onDismissPosted={() => setJustPosted(false)}
        />
      )}
      {tab === 'shifts' && <ShiftsTab persona={persona} capabilities={capabilities} />}
      {tab === 'post' && (
        <PostTab
          persona={persona}
          recipients={recipients ?? []}
          canPost={canPost}
          postPausedMessage={postPausedMessage}
          onPosted={() => {
            setJustPosted(true);
            setTab('requests');
          }}
        />
      )}
      {tab === 'schedules' && (
        <SchedulesTab canPost={canPost} capabilities={capabilities} />
      )}
    </Card>
  );
}

/** `YYYY-MM-DD` + 1 calendar day. Used to derive an overnight shift's end date
 *  from a single entered date. Parsed as UTC so the arithmetic can't be shifted
 *  by the runtime's local zone — these are plain calendar dates, not instants. */
function addOneDay(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Clock time (`HH:mm`) `hours` of REAL elapsed time after the given date+start.
 *
 *  Deliberately instant arithmetic, not clock arithmetic: `+ hours * 3_600_000`
 *  on a Date, then read back the local wall clock. Across a DST boundary the two
 *  differ — 23:00 + 8h real lands at 08:00 on the spring-forward night, not
 *  07:00. That is the honest answer for "an 8-hour shift", and because the chip
 *  writes into the End field the buyer SEES 08:00 and can correct it to 07:00 if
 *  what they actually need is coverage until day staff arrive. The form never
 *  silently decides that for them.
 *
 *  Needs the date, not just the start time, precisely because DST depends on it. */
function endTimeAfterHours(isoDate: string, startTime: string, hours: number): string {
  if (!isoDate || !startTime) return '';
  const start = new Date(`${isoDate}T${startTime}`);
  if (Number.isNaN(start.getTime())) return '';
  const end = new Date(start.getTime() + hours * 3_600_000);
  if (Number.isNaN(end.getTime())) return '';
  return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
}

/** Quick-length chips offered under the date/time row.
 *
 *  Facility = LTC shift lengths. Homecare = visit lengths, and they START AT 3h
 *  because home-care visits carry a 3-hour minimum (enforced client-side below
 *  and again in `HomecareShiftRequestsService`) — offering a 1h or 2h chip would
 *  be offering a button that always fails validation. */
const QUICK_LENGTH_HOURS: Record<'FACILITY' | 'HOMECARE', number[]> = {
  FACILITY: [4, 6, 8, 12],
  HOMECARE: [3, 4, 6, 8],
};

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      // Audit M5 (2026-09-01) — these in-card SUB-tabs used the exact same solid
      // forest pill as the primary PersonaWorkspace section tabs, so two hierarchy
      // levels looked identical (recognition-over-recall miss). Give sub-tabs a
      // lighter underline treatment so the section switcher (solid pill) and the
      // view switcher (underline) read as different tiers at a glance.
      className={
        'px-3 py-2 font-body text-sm border-b-2 -mb-px ' +
        (active
          ? 'border-forest text-forest font-medium'
          : 'border-transparent text-ink-muted hover:text-forest')
      }
    >
      {children}
    </button>
  );
}

// ---------- Requests ----------

function RequestsTab({
  persona,
  capabilities,
  justPosted = false,
  onDismissPosted,
}: {
  persona: Persona;
  capabilities?: FacilityCapability[];
  justPosted?: boolean;
  onDismissPosted?: () => void;
}) {
  const queryClient = useQueryClient();
  // Withdrawing needs SHIFT_CANCEL (same capability as cancelling a booked
  // shift). Homecare has no capability model — undefined ⇒ allowed.
  const canWithdraw = !capabilities || capabilities.includes('SHIFT_CANCEL');
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const url = persona === 'FACILITY' ? '/api/v1/me/facility/shift-requests' : '/api/v1/me/homecare/shift-requests';
  const noun = persona === 'HOMECARE' ? 'visit' : 'request';
  // 3.4 — a "Show expired & cancelled" toggle extends the current-requests list
  // backward into history. Off → `active` (live requests being matched now);
  // on → `history` (active + expired + cancelled, EXCLUDING filled — those are
  // Booked shifts — and SCHEDULED, which is future not history). The whole list
  // paginates server-side, current sorted above history, so page 1 always shows
  // what's actionable and history extends into later pages.
  const [showHistory, setShowHistory] = useState(false);
  const [page, setPage] = useState(1);
  const status = showHistory ? 'history' : 'active';
  const query = useQuery({
    queryKey: ['me', persona, 'shift-requests', status, page],
    queryFn: () =>
      unwrap(api.GET(url, { params: { query: { status, page, pageSize: PAGE_SIZE } } })),
  });

  // audit medium — clamp `page` when a background refetch shrinks the result set
  // below the current page (mirrors <ShiftsTab>). <Pager> returns null once
  // totalPages <= 1, so without this a buyer on page 2 whose list shrinks to one
  // page is stranded on an empty "no requests match" view with no pager.
  useEffect(() => {
    const tc = query.data?.totalCount;
    if (tc === undefined) return;
    const totalPages = Math.max(1, Math.ceil(tc / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [query.data?.totalCount, page]);

  // Audit #11 — auto-dismiss the post-success banner after a few seconds so it
  // doesn't linger on the list. `role="status"` on the Alert announces it once.
  useEffect(() => {
    if (!justPosted || !onDismissPosted) return;
    const t = setTimeout(onDismissPosted, 6000);
    return () => clearTimeout(t);
  }, [justPosted, onDismissPosted]);
  const postedBanner = justPosted ? (
    <Alert tone="success" title={`Your ${noun} has been posted`} data-testid="request-posted-banner">
      {persona === 'HOMECARE'
        ? 'We’ll start matching a care professional. Track it here under Open visits.'
        : 'Shifts more than two weeks out are queued and start matching about two weeks before the date — you’ll see them here until then.'}
    </Alert>
  ) : null;

  // Toggle mirrors SchedulesTab's "Show past & cancelled" reveal — a dotted
  // underline link, right-aligned in a header row — so the panel's two history
  // toggles read as one control (this was previously a checkbox: UI drift).
  const toggle = (
    <div className="flex items-center justify-end">
      <button
        type="button"
        onClick={() => {
          setShowHistory((v) => !v);
          setPage(1); // a different list — never land on a stale page
        }}
        className="font-body text-xs text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-forest"
        data-testid="requests-toggle-history"
      >
        {showHistory ? 'Hide expired & cancelled' : 'Show expired & cancelled'}
      </button>
    </div>
  );

  if (query.isPending)
    return (
      <div className="flex flex-col gap-3">
        {postedBanner}
        {toggle}
        <p className="font-body text-ink-muted">Loading…</p>
      </div>
    );
  if (query.isError)
    return (
      <div className="flex flex-col gap-3">
        {postedBanner}
        {toggle}
        <Alert tone="danger" title="Couldn't load requests">
          Refresh the page.
        </Alert>
      </div>
    );

  const items = query.data.items;
  const totalCount = query.data.totalCount;
  return (
    <div className="flex flex-col gap-3">
      {postedBanner}
      {toggle}
      {items.length === 0 ? (
        <p className="font-body text-ink-muted">
          {showHistory
            ? `No ${noun}s match.`
            : persona === 'HOMECARE'
              ? 'No open visits. Book one from the “Book a visit” tab.'
              : 'No open shifts. Post one from the “Post a shift” tab.'}
        </p>
      ) : (
        <RequestsTable
          items={items}
          persona={persona}
          onWithdraw={canWithdraw ? setWithdrawing : undefined}
        />
      )}
      {withdrawing && (
        <ReasonActionModal
          urlBase={url}
          shiftId={withdrawing}
          action="cancel"
          title={persona === 'HOMECARE' ? 'Cancel this visit request' : 'Withdraw this shift request'}
          description={
            persona === 'HOMECARE'
              ? 'Matching stops right away and care professionals who were offered this visit will see it is no longer available. There is no charge.'
              : 'Matching stops right away and care professionals who were offered this shift will see it is no longer available. There is no charge — no one has accepted it yet.'
          }
          actionLabel={persona === 'HOMECARE' ? 'Cancel request' : 'Withdraw request'}
          reasonLabel={persona === 'HOMECARE' ? 'Reason for cancelling' : 'Reason for withdrawing'}
          onClose={() => {
            setWithdrawing(null);
            // A failed withdraw usually means it was just filled — refresh so
            // the row moves to Booked shifts.
            queryClient.invalidateQueries({ queryKey: ['me', persona, 'shift-requests'] });
          }}
          onDone={() => {
            setWithdrawing(null);
            queryClient.invalidateQueries({ queryKey: ['me', persona, 'shift-requests'] });
          }}
        />
      )}
      <Pager
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        onPage={setPage}
        noun={noun}
      />
    </div>
  );
}

// Open requests render as a compact table: a request's only action is Withdraw
// (while still unfilled) and (per the active/history filter, which excludes
// FILLED) it never carries an assigned caregiver — so aligned columns read
// better than a stack of cards. The matching state shows as a pill, the same
// status language as <ShiftRow>.

/** Unfilled states a buyer can still withdraw from — mirrors the backend
 *  isCancellableRequest. Once someone accepts it is a booked shift, and "Cancel
 *  shift" (with the late-cancellation policy) applies instead. */
const WITHDRAWABLE_STATES = ['SCHEDULED', 'MATCHING', 'REBROADCASTING_T2', 'PENDING_TARGETED'];

function isWithdrawable(r: BuyerShiftRequestListItem, persona: Persona): boolean {
  if (WITHDRAWABLE_STATES.includes(r.matchingState)) return true;
  // A FUTURE pool-exhausted facility request is still re-broadcast daily, so
  // it is live; a past/homecare one is real unfilled history — leave it be.
  return (
    persona === 'FACILITY' &&
    r.matchingState === 'EXPIRED_POOL_EXHAUSTED' &&
    new Date(r.scheduledStart).getTime() > Date.now()
  );
}
function matchingStateTone(state: string): { tone: BadgeTone; variant: 'soft' | 'solid' } {
  switch (state) {
    case 'FILLED':
      return { tone: 'success', variant: 'solid' };
    case 'PENDING_TARGETED':
      return { tone: 'forest', variant: 'solid' };
    case 'SCHEDULED':
    case 'MATCHING':
    case 'REBROADCASTING_T2':
      return { tone: 'forest', variant: 'soft' };
    case 'CANCELLED_BY_BUYER':
    case 'CANCELLED_BY_OPERATOR':
      return { tone: 'danger', variant: 'soft' };
    default: // EXPIRED_* pool/time
      return { tone: 'neutral', variant: 'soft' };
  }
}

function RequestsTable({
  items,
  persona,
  onWithdraw,
}: {
  items: BuyerShiftRequestListItem[];
  persona: Persona;
  onWithdraw?: (id: string) => void;
}) {
  const withdrawLabel = persona === 'HOMECARE' ? 'Cancel request' : 'Withdraw';
  const withdrawButton = (r: BuyerShiftRequestListItem) =>
    onWithdraw && isWithdrawable(r, persona) ? (
      <button
        type="button"
        onClick={() => onWithdraw(r.id)}
        aria-label={`${withdrawLabel}: ${r.roleCode}, ${fmtRange(r.scheduledStart, r.scheduledEnd)}`}
        className="px-2.5 py-0.5 border border-ink-soft text-ink text-xs font-body rounded-md hover:text-forest"
        data-testid={`request-withdraw-${r.id}`}
      >
        {withdrawLabel}
      </button>
    ) : null;
  const th =
    'px-4 py-2.5 text-left font-body text-xs font-medium uppercase tracking-wide text-ink-muted';
  const td = 'px-4 py-3 align-top';
  return (
    <>
      {/* Desktop — aligned columns. Hidden below md, where 3 columns crush the
          "when" cell into many lines and clip the status pill. */}
      <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border bg-paper-warm">
              <th className={th}>When</th>
              <th className={th}>Role · Setting</th>
              <th className={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const pill = matchingStateTone(r.matchingState);
              return (
                <tr key={r.id} className="border-b border-border bg-paper last:border-0">
                  <td className={`${td} whitespace-nowrap font-body text-sm text-ink`}>
                    {fmtRange(r.scheduledStart, r.scheduledEnd)}
                  </td>
                  <td className={td}>
                    <div className="font-heading text-sm text-ink">
                      {r.roleCode} · {r.setting}
                    </div>
                    {persona === 'HOMECARE' && r.recipientName && (
                      <div className="font-body text-xs text-ink-muted">For {r.recipientName}</div>
                    )}
                  </td>
                  <td className={td}>
                    {/* Withdraw sits under the pill rather than in its own
                        column — a 4th column overflows the panel's card width. */}
                    <div className="flex flex-col items-start gap-1.5">
                      <Badge tone={pill.tone} variant={pill.variant}>
                        {r.matchingStateLabel}
                      </Badge>
                      {withdrawButton(r)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile — compact stacked rows (same fields, no horizontal scroll). */}
      <ul className="flex flex-col gap-2 md:hidden">
        {items.map((r) => {
          const pill = matchingStateTone(r.matchingState);
          return (
            <li key={r.id} className="flex flex-col gap-1.5 rounded-lg border border-border bg-paper p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="font-heading text-sm text-ink">
                  {r.roleCode} · {r.setting}
                </span>
                <Badge tone={pill.tone} variant={pill.variant}>
                  {r.matchingStateLabel}
                </Badge>
              </div>
              <span className="font-body text-xs text-ink-muted">
                {fmtRange(r.scheduledStart, r.scheduledEnd)}
              </span>
              {persona === 'HOMECARE' && r.recipientName && (
                <span className="font-body text-xs text-ink-muted">For {r.recipientName}</span>
              )}
              {withdrawButton(r) && <div className="pt-1">{withdrawButton(r)}</div>}
            </li>
          );
        })}
      </ul>
    </>
  );
}

// ---------- Shifts ----------

const PAGE_SIZE = 20;

// Doc 17 — "Booked shifts" uses the platform-wide shift list controls:
// [Upcoming · Past · Date range] + status filter, fixed order per view, and
// server-side paging. Scope, status and page are all server-side so they apply
// across the whole list, not just the current page.
function ShiftsTab({
  persona,
  capabilities,
}: {
  persona: Persona;
  capabilities?: FacilityCapability[];
}) {
  const url = persona === 'FACILITY' ? '/api/v1/me/facility/shifts' : '/api/v1/me/homecare/shifts';
  const noun = persona === 'HOMECARE' ? 'visit' : 'shift';
  const queryClient = useQueryClient();
  const list = useShiftListState();
  const { page, setPage } = list;

  const shifts = useQuery({
    queryKey: ['me', persona, 'shifts', list.query],
    queryFn: () => unwrap(api.GET(url, { params: { query: list.query } })),
    enabled: list.ready,
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['me', persona, 'shifts'] });

  // A buyer on page 2 who cancels the last shift there goes back a page.
  usePageClamp(page, setPage, SHIFT_LIST_PAGE_SIZE, shifts.data?.totalCount, shifts.isPlaceholderData);
  const emptyMessage = emptyShiftListMessage(list, noun);

  return (
    <div className="flex flex-col gap-4">
      <ShiftListControls state={list} />

      {!list.ready ? (
        emptyMessage && <p className="font-body text-ink-muted text-sm">{emptyMessage}</p>
      ) : shifts.isPending ? (
        <p className="font-body text-ink-muted">Loading shifts…</p>
      ) : shifts.isError ? (
        <Alert tone="danger" title="Couldn't load shifts">
          Refresh the page.
        </Alert>
      ) : shifts.data.items.length === 0 ? (
        <p className="font-body text-ink-muted text-sm">{emptyMessage}</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {shifts.data.items.map((s: BuyerShiftListItem) => (
              <ShiftRow
                key={s.id}
                s={s}
                persona={persona}
                onChange={invalidate}
                capabilities={capabilities}
              />
            ))}
          </div>
          <Pager
            page={page}
            pageSize={SHIFT_LIST_PAGE_SIZE}
            totalCount={shifts.data.totalCount}
            onPage={setPage}
            noun={noun}
          />
        </>
      )}
    </div>
  );
}

function ShiftRow({
  s,
  persona,
  onChange,
  capabilities,
}: {
  s: BuyerShiftListItem;
  persona: Persona;
  onChange: () => void;
  capabilities?: FacilityCapability[];
}) {
  const [openModal, setOpenModal] = useState<'timesheet' | 'rate' | 'cancel' | 'no-show' | null>(
    null,
  );
  // RBAC 3.2 — each action button needs its OWN atom (SHIFT_VIEW got the caller
  // this far; these are separate). undefined caps = homecare (no roles) = allowed.
  // The backend guard enforces regardless; this hides dead-end buttons that would
  // 403 (e.g. a Coordinator has no SHIFT_SIGN_TIMESHEET / SHIFT_RATE_CAREGIVER).
  const can = (c: FacilityCapability) => !capabilities || capabilities.includes(c);
  const urlBase = persona === 'FACILITY' ? '/api/v1/me/facility/shifts' : '/api/v1/me/homecare/shifts';
  return (
    <div className="rounded-lg border border-border bg-paper p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="font-heading">
          {s.roleCode} · {s.setting}
        </strong>
        <span className="font-body text-sm text-ink-muted">
          {fmtRange(s.scheduledStart, s.scheduledEnd)}
        </span>
        <ShiftStatusBadge status={s.status} scheduledEnd={s.scheduledEnd} />
      </div>
      {persona === 'HOMECARE' && s.recipientName && (
        <p className="font-body text-sm text-ink-muted mt-1">For {s.recipientName}</p>
      )}
      <CaregiverCard
        p={s.assignedCaregiver}
        collapseCredentials={s.status !== 'SCHEDULED' && s.status !== 'IN_PROGRESS'}
      />
      {s.hoursActual && (
        <p className="font-body text-xs text-ink-muted mt-1">
          {s.hoursActual} hrs billed
          {/* RBAC 3.2 — billRateAtBooking is null when this login lacks RATE_VIEW
              (e.g. a Coordinator). Show the hours but withhold the rate. */}
          {s.billRateAtBooking !== null ? ` · $${s.billRateAtBooking}/hr` : ' · rate hidden'}
        </p>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {/* A booked shift can have its timesheet signed once it has ended; the
            backend rejects a too-early signature, so status alone gates the
            button (a shift whose time has passed but is unsigned still shows
            it — that is exactly when the facility signs). */}
        {s.status === 'SCHEDULED' && (
          <>
            {can('SHIFT_SIGN_TIMESHEET') && (
              <button
                type="button"
                onClick={() => setOpenModal('timesheet')}
                className="px-3 py-1 bg-forest text-paper text-sm font-medium rounded-md"
              >
                Sign timesheet
              </button>
            )}
            {can('SHIFT_CANCEL') && (
              <button
                type="button"
                onClick={() => setOpenModal('cancel')}
                className="px-3 py-1 border border-ink-soft text-ink text-sm font-body rounded-md"
              >
                Cancel shift
              </button>
            )}
            {can('SHIFT_REPORT_NO_SHOW') && (
              <button
                type="button"
                onClick={() => setOpenModal('no-show')}
                className="px-3 py-1 border border-danger text-danger text-sm font-body rounded-md"
              >
                Report no-show
              </button>
            )}
          </>
        )}
        {s.status === 'COMPLETED' && !s.hasRating && can('SHIFT_RATE_CAREGIVER') && (
          <button
            type="button"
            onClick={() => setOpenModal('rate')}
            className="px-3 py-1 bg-forest text-paper text-sm font-medium rounded-md"
          >
            Rate professional
          </button>
        )}
      </div>
      {openModal === 'timesheet' && (
        <SignTimesheetModal
          urlBase={urlBase}
          shiftId={s.id}
          scheduledStart={s.scheduledStart}
          scheduledEnd={s.scheduledEnd}
          onClose={() => setOpenModal(null)}
          onDone={() => {
            setOpenModal(null);
            onChange();
          }}
        />
      )}
      {openModal === 'rate' && (
        <RateCaregiverModal
          urlBase={urlBase}
          shiftId={s.id}
          caregiverName={s.assignedCaregiver.displayName}
          onClose={() => setOpenModal(null)}
          onDone={() => {
            setOpenModal(null);
            onChange();
          }}
        />
      )}
      {openModal === 'cancel' && (
        <ReasonActionModal
          urlBase={urlBase}
          shiftId={s.id}
          action="cancel"
          title="Cancel shift"
          actionLabel="Cancel this shift"
          reasonLabel="Reason for cancelling"
          onClose={() => setOpenModal(null)}
          onDone={() => {
            setOpenModal(null);
            onChange();
          }}
        />
      )}
      {openModal === 'no-show' && (
        <ReasonActionModal
          urlBase={urlBase}
          shiftId={s.id}
          action="report-no-show"
          title="Report a no-show"
          actionLabel="Report no-show"
          reasonLabel="What happened?"
          onClose={() => setOpenModal(null)}
          onDone={() => {
            setOpenModal(null);
            onChange();
          }}
        />
      )}
    </div>
  );
}

function CaregiverCard({
  p,
  collapseCredentials = false,
}: {
  p: BuyerVisibleCaregiverProfile;
  collapseCredentials?: boolean;
}) {
  const credentialList = (
    <ul className="font-body text-xs text-ink-muted mt-1 flex flex-col gap-0.5">
      {p.credentials.map((c) => (
        <li key={c.type} className="flex items-center gap-1.5">
          <StatusBadge
            status={c.isCurrent ? 'Valid' : 'Expired'}
            tone={c.isCurrent ? 'success' : 'danger'}
          />
          <span>{c.label}</span>
          {c.expiresAt && <span>· expires {c.expiresAt}</span>}
        </li>
      ))}
    </ul>
  );
  // Doc 16 — optional certificates the care professional added themselves:
  // names + expiry only (TrueCare sends the document on request). Labelled
  // "self-reported" because TrueCare doesn't review them.
  // `?? []` / `?? 0`: during a deploy the new portal can briefly talk to an API
  // that predates the fields — render nothing rather than crash the shift card.
  // Certificates added under "Other" aren't named here (their name is
  // unreviewed free text) — only counted; TrueCare shares details on request.
  const additional = p.additionalCertificates ?? [];
  const otherCount = p.otherCertificateCount ?? 0;
  const certificateParts = [
    ...additional.map((c) =>
      c.isExpired ? `${c.name} (expired)` : c.expiresAt ? `${c.name} (expires ${c.expiresAt})` : c.name,
    ),
    ...(otherCount > 0
      ? [`+ ${otherCount} other certificate${otherCount === 1 ? '' : 's'} (ask TrueCare™)`]
      : []),
  ];
  const extraCertificates =
    certificateParts.length > 0 ? (
      <p className="font-body text-xs text-ink-muted mt-1" data-testid="caregiver-card-certificates">
        <span className="text-ink">Other certificates (self-reported):</span>{' '}
        {certificateParts.join(' · ')}
      </p>
    ) : null;
  return (
    <div className="mt-2 border-t border-ink-soft pt-2">
      <p className="font-body text-sm">
        <strong>{p.displayName}</strong> · {p.roleCodeForThisShift}
      </p>
      {/* On terminal shifts the buyer can't act on credentials, so collapse the
          matrix behind a disclosure rather than burying the shift's own facts. */}
      {collapseCredentials ? (
        <details className="mt-1">
          <summary className="cursor-pointer font-body text-xs text-ink-muted">
            Credentials at time of shift
          </summary>
          {credentialList}
          {extraCertificates}
        </details>
      ) : (
        <>
          {credentialList}
          {extraCertificates}
        </>
      )}
      <p className="font-body text-xs text-ink-muted mt-1">
        {p.monthsOnPlatform < 1
          ? 'New to TrueCare™'
          : `On TrueCare™ for ${p.monthsOnPlatform} ${p.monthsOnPlatform === 1 ? 'month' : 'months'}`}
      </p>
    </div>
  );
}

// ---------- Post ----------

function PostTab({
  persona,
  recipients,
  canPost,
  postPausedMessage,
  onPosted,
}: {
  persona: Persona;
  recipients: Recipient[];
  canPost: boolean;
  postPausedMessage?: string;
  onPosted: () => void;
}) {
  const queryClient = useQueryClient();
  const url = persona === 'FACILITY' ? '/api/v1/me/facility/shift-requests' : '/api/v1/me/homecare/shift-requests';
  const [roleId, setRoleId] = useState('');
  // Setting is derived from who's posting — a facility is always LTC, a homecare
  // client always HOMECARE. Not a user choice, so it isn't a form field.
  const setting: 'LTC' | 'HOMECARE' = persona === 'FACILITY' ? 'LTC' : 'HOMECARE';
  // A shift is entered as ONE date + two times (see the field row below).
  // `scheduledStart` / `scheduledEnd` are DERIVED from these into the
  // `YYYY-MM-DDTHH:mm` local form the rest of this component already expects,
  // so submit + validation are unchanged.
  const [shiftDate, setShiftDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // End at or before start ⇒ the shift runs past midnight into the next day.
  const endsNextDay = Boolean(startTime && endTime && endTime <= startTime);
  const endDate = endsNextDay ? addOneDay(shiftDate) : shiftDate;
  const scheduledStart = shiftDate && startTime ? `${shiftDate}T${startTime}` : '';
  const scheduledEnd = endDate && endTime ? `${endDate}T${endTime}` : '';

  // Plain-language readout under the row: duration, plus the next-day date
  // spelled out when it applies. Makes the derived overnight visible instead of
  // magic, and lets the buyer sanity-check the length before posting.
  //
  // Real elapsed hours between the two derived instants — the SAME quantity the
  // backend bills and pays on (`(scheduledEnd - scheduledStart)` in ms), so what
  // the buyer reads here is what they are charged for. Null until both ends are
  // set and the window is positive.
  const durationHours = (() => {
    if (!scheduledStart || !scheduledEnd) return null;
    const ms = new Date(scheduledEnd).getTime() - new Date(scheduledStart).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    return ms / 3_600_000;
  })();

  const shiftSummary = (() => {
    if (durationHours === null) return '';
    const pretty = Number.isInteger(durationHours)
      ? `${durationHours}h`
      : `${durationHours.toFixed(2).replace(/\.?0+$/, '')}h`;
    return endsNextDay ? `${pretty} · ends next day (${endDate})` : pretty;
  })();

  // Quick-length chips fill the End field rather than replacing it: length is the
  // convenient way to THINK about a shift, but the end time is what an LTC
  // coverage window is actually anchored to (nights run to 07:00 because day
  // staff arrive then), and it's what the backend stores. Chips need a date to
  // be meaningful — DST makes "start + 8h" date-dependent.
  const quickLengths = QUICK_LENGTH_HOURS[persona];
  const canQuickFill = Boolean(shiftDate && startTime);
  // Phase 21 (3.1-1) — facility headcount: "N caregivers for this slot" posts N
  // independent requests. Homecare is per-recipient (single), so no field there.
  const [caregiversNeeded, setCaregiversNeeded] = useState(1);
  // Phase 21 (3.1-8) — optional facility-preferred caregiver (priority offer +
  // 24h fallback). Chosen from those the facility has worked with.
  const [preferredCaregiverId, setPreferredCaregiverId] = useState('');
  const [recipientId, setRecipientId] = useState('');
  const [specialConstraints, setSpecialConstraints] = useState('');
  // Client-side validation error (e.g., home-care 3-hour minimum). Shown
  // inline; cleared on a successful submit attempt.
  const [validationError, setValidationError] = useState<string | null>(null);
  // 2026-10-28 (operator) — posting goes through a review dialog so a buyer
  // confirms the details before anything broadcasts; mis-posts were the
  // motivating case for the withdraw flow.
  const [reviewing, setReviewing] = useState(false);

  // Caregiver categories the buyer can request — data-driven from the Role
  // table (setting-filtered on the backend), so new roles appear here with no
  // code change.
  const rolesQuery = useQuery({
    queryKey: ['shift-request-roles', persona],
    queryFn: () =>
      persona === 'FACILITY'
        ? unwrap(api.GET('/api/v1/me/facility/shift-requests/roles'))
        : unwrap(api.GET('/api/v1/me/homecare/shift-requests/roles')),
  });
  const roleOptions = rolesQuery.data?.roles ?? [];

  // Phase 21 (3.1-8) — the facility's worked-with care professionals for the
  // optional preferred-caregiver picker. Facility only.
  const workedWithQuery = useQuery({
    queryKey: ['worked-with-caregivers'],
    enabled: persona === 'FACILITY',
    queryFn: () =>
      unwrap(api.GET('/api/v1/me/facility/shift-requests/worked-with-caregivers')),
  });
  const workedWith = workedWithQuery.data?.caregivers ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST(url, {
          body: {
            roleId,
            setting,
            scheduledStart: new Date(scheduledStart).toISOString(),
            scheduledEnd: new Date(scheduledEnd).toISOString(),
            requestingEntityType: persona === 'FACILITY' ? 'FACILITY' : 'HOMECARE_CLIENT',
            ...(persona === 'HOMECARE' ? { recipientId } : {}),
            ...(persona === 'FACILITY' ? { caregiversNeeded } : {}),
            ...(persona === 'FACILITY' && preferredCaregiverId
              ? { preferredCaregiverId }
              : {}),
            specialConstraints: specialConstraints.trim() || undefined,
            requiredSkills: [],
          },
        }),
      ),
    onSuccess: () => {
      setReviewing(false);
      queryClient.invalidateQueries({ queryKey: ['me', persona, 'shift-requests'] });
      onPosted();
    },
    // A headcount post creates N requests one by one; a failure part-way leaves
    // the earlier ones live. Refresh the list so they show up, and the dialog
    // warns before a retry re-posts them.
    onError: () => queryClient.invalidateQueries({ queryKey: ['me', persona, 'shift-requests'] }),
  });

  const selectedRecipient = recipients.find((r) => r.id === recipientId);
  // An ungeocoded recipient has no lat/lng, so matching would reject every
  // caregiver (request_geo_missing) — the request would be guaranteed
  // unmatchable. Block it client-side; the backend enforces the same guard.
  const recipientUngeocoded =
    persona === 'HOMECARE' && !!selectedRecipient && !selectedRecipient.geocoded;

  // What's still blocking submission — surfaced under the disabled button so the
  // buyer knows why they can't post yet, instead of a silently-disabled CTA.
  const missing: string[] = [];
  if (!roleId) missing.push('a category');
  if (!shiftDate) missing.push('a date');
  if (!startTime) missing.push('a start time');
  if (!endTime) missing.push('an end time');
  if (persona === 'HOMECARE' && !recipientId) missing.push('a recipient');

  const handleSubmit = () => {
    setValidationError(null);
    if (recipientUngeocoded) {
      setValidationError(
        "This recipient's address hasn't been verified yet, so we can't match a professional. Edit the address under Recipients, then try again.",
      );
      return;
    }
    // Home-care visits carry a 3-hour minimum (facility shifts have none).
    if (persona === 'HOMECARE') {
      const start = new Date(scheduledStart).getTime();
      const end = new Date(scheduledEnd).getTime();
      if (Number.isFinite(start) && Number.isFinite(end)) {
        const hours = (end - start) / (1000 * 60 * 60);
        if (hours < 3) {
          setValidationError('Home-care visits have a 3-hour minimum.');
          return;
        }
      }
    }
    createMut.reset();
    setReviewing(true);
  };

  if (!canPost) {
    return (
      <Card padding="md" className="bg-paper-warm">
        <p className="font-body text-sm text-ink-muted" data-testid="post-paused-message">
          {postPausedMessage ??
            (persona === 'HOMECARE'
              ? 'Booking is paused while your account is not active. Contact TrueCare™ to resume.'
              : 'Shift posting is paused on this account. Contact TrueCare™ to resume.')}
        </p>
      </Card>
    );
  }

  return (
    <Card padding="md" className="bg-paper-warm">
      <p className="font-body text-sm text-ink-muted mb-3">
        {persona === 'HOMECARE'
          ? 'Book a visit to start matching with care professionals. Search begins immediately; top-3 care professionals within their declared travel radius receive an offer. Home-care visits have a 3-hour minimum.'
          : 'Post a shift to start matching with care professionals. Search begins immediately; top-3 care professionals within their declared travel radius receive an offer.'}
      </p>
      <div className="flex flex-col gap-3">
        <Field label="Professional category *">
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full px-2 py-1 border border-ink-soft rounded bg-paper"
            data-testid="post-shift-role"
          >
            <option value="">
              {rolesQuery.isLoading ? 'Loading categories…' : 'Select a category…'}
            </option>
            {roleOptions.map((r: { id: string; name: string; code: string }) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.code})
              </option>
            ))}
          </select>
        </Field>
        {/* One row: date once, then the two times — a shift is described as
            "Tuesday, 7 to 3", not as two full timestamps. The old pair of
            `datetime-local` inputs made you enter the SAME date twice (and let
            you silently enter two different ones), and its picker/typing
            ergonomics vary badly across browsers. Overnight is DERIVED (end at
            or before start ⇒ ends next day) rather than a checkbox the user
            must remember — the hint below states it plainly so it's visible
            and self-correcting. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
          <Field label="Date *">
            <DateInput
              value={shiftDate}
              onChange={setShiftDate}
              data-testid="post-shift-date"
            />
          </Field>
          <Field label="Start *">
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded"
              data-testid="post-shift-start"
            />
          </Field>
          <Field label="End *">
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded"
              data-testid="post-shift-end"
            />
          </Field>
        </div>
        {/* Quick length — one click for the common case. Sets End; does not
            replace it. See `endTimeAfterHours` for why this is instant
            arithmetic and why the buyer keeps the last word on DST nights. */}
        <div className="-mt-1 flex flex-wrap items-center gap-1.5" data-testid="post-shift-quick-length">
          <span className="font-body text-xs text-ink-muted">
            {persona === 'HOMECARE' ? 'Visit length:' : 'Shift length:'}
          </span>
          {quickLengths.map((h) => (
            <button
              key={h}
              type="button"
              disabled={!canQuickFill}
              aria-pressed={durationHours === h}
              onClick={() => setEndTime(endTimeAfterHours(shiftDate, startTime, h))}
              title={canQuickFill ? `End ${h}h after ${startTime}` : 'Pick a date and start time first'}
              className={
                'px-2.5 py-0.5 rounded-md font-body text-xs border disabled:opacity-40 disabled:cursor-not-allowed ' +
                (durationHours === h
                  ? 'bg-forest text-paper border-forest'
                  : 'bg-paper text-ink-muted border-ink-soft hover:enabled:text-forest')
              }
              data-testid={`post-shift-length-${h}h`}
            >
              {h}h
            </button>
          ))}
        </div>
        <p className="-mt-1 font-body text-xs text-ink-muted" data-testid="post-shift-when-hint">
          {shiftSummary ? (
            <>
              <strong className="text-ink">{shiftSummary}</strong> · Eastern
              (America/Toronto)
            </>
          ) : (
            'Times are Eastern (America/Toronto).'
          )}
        </p>
        {persona === 'FACILITY' && (
          <Field label="Care professionals needed">
            <input
              type="number"
              min={1}
              max={20}
              value={caregiversNeeded}
              onChange={(e) =>
                setCaregiversNeeded(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
              }
              className="w-24 px-2 py-1 border border-border rounded"
              data-testid="post-shift-caregivers-needed"
            />
          </Field>
        )}
        {persona === 'FACILITY' && workedWith.length > 0 && (
          <Field label="Preferred care professional (optional)">
            <select
              value={preferredCaregiverId}
              onChange={(e) => setPreferredCaregiverId(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded bg-paper"
              data-testid="post-shift-preferred-caregiver"
            >
              <option value="">No preference — match anyone eligible</option>
              {workedWith.map((cg: { id: string; name: string; roleCodes: string[] }) => (
                <option key={cg.id} value={cg.id}>
                  {cg.name}
                  {cg.roleCodes.length > 0 ? ` (${cg.roleCodes.join(', ')})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 font-body text-xs text-ink-muted">
              They get first refusal for 24 hours, then it opens to everyone.
            </p>
          </Field>
        )}
        {persona === 'HOMECARE' && (
          <Field label="Recipient *">
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded bg-paper"
            >
              <option value="">Select recipient…</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id} disabled={!r.geocoded}>
                  {r.name}
                  {!r.geocoded ? ' — address not verified yet' : ''}
                </option>
              ))}
            </select>
          </Field>
        )}
        {recipientUngeocoded && (
          <p className="font-body text-xs text-danger">
            This recipient's address hasn't been verified yet. Edit it under
            Recipients before booking a visit.
          </p>
        )}
        <Field label="Notes (non-clinical)">
          <textarea
            value={specialConstraints}
            onChange={(e) => setSpecialConstraints(e.target.value)}
            rows={2}
            className="w-full px-2 py-1 border border-border rounded bg-paper"
          />
        </Field>
      </div>
      {validationError && (
        <Alert tone="danger" title="Check your details">
          {validationError}
        </Alert>
      )}
      {reviewing && (
        <ConfirmPostModal
          persona={persona}
          details={[
            {
              label: 'Category',
              value: (() => {
                const r = roleOptions.find((o: { id: string }) => o.id === roleId) as
                  | { name: string; code: string }
                  | undefined;
                return r ? `${r.name} (${r.code})` : '';
              })(),
            },
            {
              label: 'When',
              value: fmtRange(new Date(scheduledStart).toISOString(), new Date(scheduledEnd).toISOString()),
            },
            { label: 'Length', value: shiftSummary },
            ...(durationHours !== null
              ? [
                  {
                    label: 'Default break',
                    value:
                      defaultBreakMinutes(durationHours) > 0
                        ? `${defaultBreakMinutes(durationHours)} min (unpaid)`
                        : 'None',
                  },
                ]
              : []),
            ...(persona === 'FACILITY'
              ? [{ label: 'Care professionals needed', value: String(caregiversNeeded) }]
              : []),
            ...(persona === 'FACILITY' && preferredCaregiverId
              ? [
                  {
                    label: 'Preferred care professional',
                    value: workedWith.find((c: { id: string }) => c.id === preferredCaregiverId)?.name ?? '',
                  },
                ]
              : []),
            ...(persona === 'HOMECARE' && selectedRecipient
              ? [{ label: 'Recipient', value: selectedRecipient.name }]
              : []),
            ...(specialConstraints.trim() ? [{ label: 'Notes', value: specialConstraints.trim() }] : []),
          ]}
          count={persona === 'FACILITY' ? caregiversNeeded : 1}
          pending={createMut.isPending}
          error={createMut.isError ? messageOf(createMut.error) : null}
          onConfirm={() => createMut.mutate()}
          // Not while posting: closing mid-request would hide a failure (the
          // error renders only in this dialog).
          onClose={() => {
            if (!createMut.isPending) setReviewing(false);
          }}
        />
      )}
      <button
        type="button"
        disabled={
          createMut.isPending ||
          !roleId ||
          !scheduledStart ||
          !scheduledEnd ||
          (persona === 'HOMECARE' && !recipientId) ||
          recipientUngeocoded
        }
        onClick={handleSubmit}
        className="mt-3 px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
        data-testid="post-shift-submit"
      >
        {persona === 'HOMECARE' ? 'Review booking' : 'Review and post'}
      </button>
      {missing.length > 0 && !createMut.isPending && (
        <p className="mt-2 font-body text-xs text-ink-muted">
          Select {missing.join(', ')} to continue.
        </p>
      )}
    </Card>
  );
}

/** Review step before a shift request is created: the buyer reads back what
 *  they entered and confirms. Nothing is posted (and nothing broadcasts) until
 *  "Post"/"Book" here is pressed; "Edit details" returns to the form intact. */
function ConfirmPostModal({
  persona,
  details,
  count,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  persona: Persona;
  details: { label: string; value: string }[];
  count: number;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const isHomecare = persona === 'HOMECARE';
  return (
    <ModalShell title={isHomecare ? 'Confirm your visit' : 'Confirm shift details'} onClose={onClose}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-body text-sm" data-testid="post-confirm-details">
        {details.map((d) => (
          <div key={d.label} className="contents">
            <dt className="text-ink-muted">{d.label}</dt>
            <dd className="text-ink break-words">{d.value}</dd>
          </div>
        ))}
      </dl>
      <p className="font-body text-xs text-ink-muted">
        {isHomecare
          ? 'Matching starts as soon as you book. You can cancel at no charge until a care professional accepts.'
          : `${count > 1 ? `This posts ${count} separate shifts. ` : ''}Shifts in the next two weeks start matching right away; later ones are queued and start about two weeks before. You can withdraw ${count > 1 ? 'any of them' : 'it'} at no charge until a care professional accepts.`}
      </p>
      {error && (
        <Alert tone="danger" title={isHomecare ? "Couldn't book" : "Couldn't post"}>
          {error}
          {count > 1 &&
            ' Some of these shifts may already have been posted — check Open shifts before trying again.'}
        </Alert>
      )}
      <div className="mt-1 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className="px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
          data-testid="post-confirm-submit"
        >
          {pending
            ? isHomecare
              ? 'Booking…'
              : 'Posting…'
            : isHomecare
              ? 'Book visit'
              : count > 1
                ? `Post ${count} shifts`
                : 'Post shift'}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={onClose}
          className="px-4 py-1.5 border border-ink-soft text-ink text-sm rounded-md disabled:opacity-50"
          data-testid="post-confirm-edit"
        >
          Edit details
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- Recurring schedules (3.1d) ----------

// ISO weekday order: 1 = Mon … 7 = Sun. The picker + the day-list label both
// read from this so a schedule's days always render in week order.
const WEEKDAYS: { iso: number; short: string; long: string }[] = [
  { iso: 1, short: 'Mon', long: 'Monday' },
  { iso: 2, short: 'Tue', long: 'Tuesday' },
  { iso: 3, short: 'Wed', long: 'Wednesday' },
  { iso: 4, short: 'Thu', long: 'Thursday' },
  { iso: 5, short: 'Fri', long: 'Friday' },
  { iso: 6, short: 'Sat', long: 'Saturday' },
  { iso: 7, short: 'Sun', long: 'Sunday' },
];

function weekdayLabel(days: number[]): string {
  return WEEKDAYS.filter((d) => days.includes(d.iso))
    .map((d) => d.short)
    .join(', ');
}

/** Facility-only recurring schedules: a create form stacked over the list of
 *  existing schedules. Each schedule eagerly materialized N occurrences on the
 *  server; the list shows the count + lets the operator expand to the
 *  occurrences and cancel future unfilled ones. */
function SchedulesTab({
  canPost,
  capabilities,
}: {
  canPost: boolean;
  capabilities?: FacilityCapability[];
}) {
  const queryClient = useQueryClient();
  // Audit #20 — create needs SHIFT_POST, cancel needs SHIFT_CANCEL. Undefined
  // caps (homecare — n/a here since schedules are facility-only) ⇒ allowed.
  const canCreate = capabilities ? capabilities.includes('SHIFT_POST') : true;
  const canCancel = capabilities ? capabilities.includes('SHIFT_CANCEL') : true;
  // Hidden by default so the list stays short: schedules that are CANCELLED
  // **or** past their end date. There is no COMPLETED status — an elapsed
  // schedule stays ACTIVE forever — so "past" is derived from rangeEnd
  // server-side. The backend always returns `inactiveCount` so this toggle can
  // be labelled without fetching them.
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: ['me', 'FACILITY', 'schedules', showInactive, page],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/me/facility/schedules', {
          params: { query: { includeInactive: showInactive, page, pageSize: SCHEDULES_PAGE_SIZE } },
        }),
      ),
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['me', 'FACILITY', 'schedules'] });

  const inactiveCount = list.data?.inactiveCount ?? 0;

  // Toggling the filter changes the result set, so reset to page 1 — otherwise
  // skip could land past the (smaller) filtered totalCount.
  const toggleInactive = () => {
    setShowInactive((v) => !v);
    setPage(1);
  };

  // If a page empties out (e.g. after cancelling the last row on it), step back.
  useEffect(() => {
    const tc = list.data?.totalCount;
    if (tc !== undefined && page > 1 && (page - 1) * SCHEDULES_PAGE_SIZE >= tc) {
      setPage((p) => Math.max(1, p - 1));
    }
  }, [list.data?.totalCount, page]);

  return (
    <div className="flex flex-col gap-6">
      {/* Audit #20 — the create form needs SHIFT_POST. A view-only user
          (SHIFT_VIEW, no SHIFT_POST) sees the schedule LIST but no form + no
          "paused" card (that's only relevant to someone who could create). */}
      {canCreate && canPost ? (
        <NewScheduleForm onCreated={invalidate} />
      ) : canCreate && !canPost ? (
        <Card padding="md" className="bg-paper-warm">
          <p className="font-body text-sm text-ink-muted">
            Scheduling is paused on this account. Contact TrueCare™ to resume.
          </p>
        </Card>
      ) : null}

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h4 className="font-heading text-base text-forest">Your schedules</h4>
          {(inactiveCount > 0 || showInactive) && (
            <button
              type="button"
              onClick={toggleInactive}
              className="font-body text-xs text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-forest"
              data-testid="schedules-toggle-inactive"
            >
              {showInactive ? 'Hide past & cancelled' : `Show past & cancelled (${inactiveCount})`}
            </button>
          )}
        </div>
        {list.isPending ? (
          <p className="font-body text-ink-muted text-sm">Loading schedules…</p>
        ) : list.isError ? (
          <Alert tone="danger" title="Couldn't load schedules">
            Refresh the page.
          </Alert>
        ) : list.data.schedules.length === 0 ? (
          <p className="font-body text-ink-muted text-sm">
            {showInactive
              ? 'No recurring schedules.'
              : 'No current recurring schedules. Create one above to post a repeating shift across a date range.'}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {list.data.schedules.map((s: ScheduleListItem) => (
                <ScheduleRow key={s.id} s={s} onChange={invalidate} canCancel={canCancel} />
              ))}
            </div>
            <Pager
              page={page}
              pageSize={SCHEDULES_PAGE_SIZE}
              totalCount={list.data.totalCount}
              onPage={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

const SCHEDULES_PAGE_SIZE = 20;

interface ScheduleListItem {
  id: string;
  roleCode: string;
  setting: string;
  daysOfWeek: number[];
  startTimeLocal: string;
  endTimeLocal: string;
  rangeStart: string;
  rangeEnd: string;
  caregiversPerOccurrence: number;
  status: 'ACTIVE' | 'CANCELLED';
  occurrenceCount: number;
}

function NewScheduleForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [startTimeLocal, setStartTimeLocal] = useState('07:00');
  const [endTimeLocal, setEndTimeLocal] = useState('15:00');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [caregiversPerOccurrence, setCaregiversPerOccurrence] = useState(1);
  const [preferredCaregiverId, setPreferredCaregiverId] = useState('');
  const [specialConstraints, setSpecialConstraints] = useState('');

  const rolesQuery = useQuery({
    queryKey: ['shift-request-roles', 'FACILITY'],
    queryFn: () => unwrap(api.GET('/api/v1/me/facility/shift-requests/roles')),
  });
  const roleOptions = rolesQuery.data?.roles ?? [];

  const workedWithQuery = useQuery({
    queryKey: ['worked-with-caregivers'],
    queryFn: () => unwrap(api.GET('/api/v1/me/facility/shift-requests/worked-with-caregivers')),
  });
  const workedWith = workedWithQuery.data?.caregivers ?? [];

  const reset = () => {
    setRoleId('');
    setDays([]);
    setStartTimeLocal('07:00');
    setEndTimeLocal('15:00');
    setRangeStart('');
    setRangeEnd('');
    setCaregiversPerOccurrence(1);
    setPreferredCaregiverId('');
    setSpecialConstraints('');
  };

  const createMut = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/me/facility/schedules', {
          body: {
            roleId,
            daysOfWeek: days,
            startTimeLocal,
            endTimeLocal,
            rangeStart,
            rangeEnd,
            caregiversPerOccurrence,
            ...(preferredCaregiverId ? { preferredCaregiverId } : {}),
            specialConstraints: specialConstraints.trim() || undefined,
            requiredSkills: [],
          },
        }),
      ),
    onSuccess: () => {
      reset();
      setOpen(false);
      onCreated();
    },
  });

  const toggleDay = (iso: number) =>
    setDays((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort((a, b) => a - b),
    );

  const missing: string[] = [];
  if (!roleId) missing.push('a category');
  if (days.length === 0) missing.push('at least one weekday');
  if (!rangeStart) missing.push('a start date');
  if (!rangeEnd) missing.push('an end date');
  const canSubmit = missing.length === 0 && !createMut.isPending;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md"
        data-testid="new-schedule-open"
      >
        + New recurring schedule
      </button>
    );
  }

  return (
    <Card padding="md" className="bg-paper-warm">
      <p className="font-body text-sm text-ink-muted mb-3">
        A recurring schedule posts the same shift on the weekdays you pick, across
        a date range (up to 90 days). Each occurrence is matched independently as
        it comes within two weeks of its start. Times are Eastern (America/Toronto).
      </p>
      <div className="flex flex-col gap-3">
        <Field label="Professional category *">
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full px-2 py-1 border border-ink-soft rounded bg-paper"
            data-testid="schedule-role"
          >
            <option value="">
              {rolesQuery.isLoading ? 'Loading categories…' : 'Select a category…'}
            </option>
            {roleOptions.map((r: { id: string; name: string; code: string }) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.code})
              </option>
            ))}
          </select>
        </Field>

        <div>
          <span className="font-body text-xs text-ink-muted">Repeats on *</span>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="schedule-days">
            {WEEKDAYS.map((d) => {
              const active = days.includes(d.iso);
              return (
                <button
                  key={d.iso}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleDay(d.iso)}
                  className={
                    'px-3 py-1 rounded-md font-body text-sm border ' +
                    (active
                      ? 'bg-forest text-paper border-forest'
                      : 'bg-paper text-ink-muted border-ink-soft hover:text-forest')
                  }
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Start time *">
            <input
              type="time"
              value={startTimeLocal}
              onChange={(e) => setStartTimeLocal(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded"
            />
          </Field>
          <Field label="End time *">
            <input
              type="time"
              value={endTimeLocal}
              onChange={(e) => setEndTimeLocal(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded"
            />
          </Field>
        </div>
        <p className="-mt-1 font-body text-xs text-ink-muted">
          An end time earlier than the start time is treated as an overnight shift
          ending the next day.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="From date *">
            <DateInput value={rangeStart} onChange={setRangeStart} />
          </Field>
          <Field label="To date *">
            <DateInput value={rangeEnd} onChange={setRangeEnd} />
          </Field>
        </div>

        <Field label="Care professionals needed (per occurrence)">
          <input
            type="number"
            min={1}
            max={20}
            value={caregiversPerOccurrence}
            onChange={(e) =>
              setCaregiversPerOccurrence(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
            }
            className="w-24 px-2 py-1 border border-border rounded"
          />
        </Field>

        {workedWith.length > 0 && (
          <Field label="Preferred care professional (optional)">
            <select
              value={preferredCaregiverId}
              onChange={(e) => setPreferredCaregiverId(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded bg-paper"
            >
              <option value="">No preference — match anyone eligible</option>
              {workedWith.map((cg: { id: string; name: string; roleCodes: string[] }) => (
                <option key={cg.id} value={cg.id}>
                  {cg.name}
                  {cg.roleCodes.length > 0 ? ` (${cg.roleCodes.join(', ')})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 font-body text-xs text-ink-muted">
              On each occurrence they get first refusal for 24 hours, then it opens
              to everyone.
            </p>
          </Field>
        )}

        <Field label="Notes (non-clinical)">
          <textarea
            value={specialConstraints}
            onChange={(e) => setSpecialConstraints(e.target.value)}
            rows={2}
            className="w-full px-2 py-1 border border-border rounded bg-paper"
          />
        </Field>
      </div>

      {createMut.isError && (
        <Alert tone="danger" title="Couldn't create schedule">
          {messageOf(createMut.error)}
        </Alert>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => createMut.mutate()}
          className="px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
          data-testid="schedule-submit"
        >
          Create schedule
        </button>
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className="px-4 py-1.5 border border-ink-soft text-ink text-sm font-body rounded-md"
        >
          Cancel
        </button>
      </div>
      {missing.length > 0 && !createMut.isPending && (
        <p className="mt-2 font-body text-xs text-ink-muted">
          Select {missing.join(', ')} to continue.
        </p>
      )}
    </Card>
  );
}

function ScheduleRow({
  s,
  onChange,
  canCancel,
}: {
  s: ScheduleListItem;
  onChange: () => void;
  canCancel: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['me', 'FACILITY', 'schedule', s.id],
    enabled: expanded,
    queryFn: () =>
      unwrap(api.GET('/api/v1/me/facility/schedules/{id}', { params: { path: { id: s.id } } })),
  });

  const cancelMut = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/me/facility/schedules/{id}/cancel', {
          params: { path: { id: s.id } },
        }),
      ),
    onSuccess: () => {
      setConfirmCancel(false);
      queryClient.invalidateQueries({ queryKey: ['me', 'FACILITY', 'schedule', s.id] });
      onChange();
    },
  });

  return (
    <Card padding="md" className="bg-paper">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="font-heading text-base">
          {s.roleCode} · {s.setting}
        </strong>
        <StatusBadge
          status={s.status === 'ACTIVE' ? 'Active' : 'Cancelled'}
          tone={s.status === 'ACTIVE' ? 'success' : 'neutral'}
        />
      </div>
      <p className="font-body text-sm text-ink-muted mt-1">
        {weekdayLabel(s.daysOfWeek)} · {s.startTimeLocal}–{s.endTimeLocal}
      </p>
      <p className="font-body text-sm text-ink-muted">
        {s.rangeStart} → {s.rangeEnd} · {s.occurrenceCount} shift
        {s.occurrenceCount === 1 ? '' : 's'}
        {s.caregiversPerOccurrence > 1 ? ` · ${s.caregiversPerOccurrence} per occurrence` : ''}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="px-3 py-1 border border-ink-soft text-ink text-sm font-body rounded-md"
          aria-expanded={expanded}
        >
          {expanded ? 'Hide occurrences' : 'View occurrences'}
        </button>
        {/* Audit #20 — cancelling a schedule needs SHIFT_CANCEL (mirrors the
            per-shift Cancel gate). A view-only Approver sees the row + can
            expand occurrences, but not cancel. */}
        {canCancel && s.status === 'ACTIVE' && !confirmCancel && (
          <button
            type="button"
            onClick={() => setConfirmCancel(true)}
            className="px-3 py-1 border border-danger text-danger text-sm font-body rounded-md"
          >
            Cancel schedule
          </button>
        )}
        {canCancel && s.status === 'ACTIVE' && confirmCancel && (
          <>
            <button
              type="button"
              disabled={cancelMut.isPending}
              onClick={() => cancelMut.mutate()}
              className="px-3 py-1 bg-danger text-paper text-sm font-medium rounded-md disabled:opacity-50"
            >
              Cancel future shifts
            </button>
            <button
              type="button"
              onClick={() => setConfirmCancel(false)}
              className="px-3 py-1 border border-ink-soft text-ink text-sm font-body rounded-md"
            >
              Keep schedule
            </button>
          </>
        )}
      </div>
      {confirmCancel && (
        <p className="mt-2 font-body text-xs text-ink-muted">
          Cancels every upcoming unfilled occurrence. Shifts already filled stay
          booked.
        </p>
      )}
      {cancelMut.isError && (
        <Alert tone="danger" title="Couldn't cancel">
          {messageOf(cancelMut.error)}
        </Alert>
      )}

      {expanded && (
        <div className="mt-3 border-t border-ink-soft pt-3">
          {detail.isPending ? (
            <p className="font-body text-sm text-ink-muted">Loading occurrences…</p>
          ) : detail.isError ? (
            <Alert tone="danger" title="Couldn't load occurrences">
              Refresh the page.
            </Alert>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {detail.data.occurrences.map((o: ScheduleOccurrence) => (
                <li
                  key={o.shiftRequestId}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 font-body text-sm"
                >
                  <span>{fmtRange(o.scheduledStart, o.scheduledEnd)}</span>
                  <span className="text-ink-muted text-xs">{occurrenceStateLabel(o.matchingState)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

interface ScheduleOccurrence {
  shiftRequestId: string;
  scheduledStart: string;
  scheduledEnd: string;
  matchingState: string;
}

// Buyer-facing plain-language label for an occurrence's matching state. Mirrors
// the server's matchingStateLabel intent — schedules add SCHEDULED (queued,
// not yet within the two-week broadcast horizon).
function occurrenceStateLabel(state: string): string {
  switch (state) {
    case 'SCHEDULED':
      return 'Queued — opens ~2 weeks out';
    case 'MATCHING':
    case 'REBROADCASTING_T2':
    case 'PENDING_TARGETED':
      return 'Matching…';
    case 'FILLED':
      return 'Filled';
    case 'EXPIRED_POOL_EXHAUSTED':
    case 'EXPIRED_TIME_HIT':
      return 'Unfilled — expired';
    case 'CANCELLED_BY_BUYER':
    case 'CANCELLED_BY_OPERATOR':
      return 'Cancelled';
    default:
      return state;
  }
}

// ---------- Modals ----------

function SignTimesheetModal({
  urlBase,
  shiftId,
  scheduledStart,
  scheduledEnd,
  onClose,
  onDone,
}: {
  urlBase: string;
  shiftId: string;
  scheduledStart: string;
  scheduledEnd: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [actualStart, setActualStart] = useState(toLocal(scheduledStart));
  const [actualEnd, setActualEnd] = useState(toLocal(scheduledEnd));
  // Break defaults from the ACTUAL length entered (>5h → 30 min, else 0) and
  // follows edits to start/end — until the signer types a break, which then sticks.
  const [breakOverride, setBreakOverride] = useState<number | null>(null);
  const actualHours = (new Date(actualEnd).getTime() - new Date(actualStart).getTime()) / 3_600_000;
  const breakMinutes = breakOverride ?? defaultBreakMinutes(actualHours);
  const [notes, setNotes] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      unwrap(
        // Facility + homecare share this body; type-check against the facility path.
        api.POST(`${urlBase}/{id}/sign-timesheet` as '/api/v1/me/facility/shifts/{id}/sign-timesheet', {
          params: { path: { id: shiftId } },
          body: {
            actualStart: new Date(actualStart).toISOString(),
            actualEnd: new Date(actualEnd).toISOString(),
            breakMinutes,
            completionNotes: notes || undefined,
          },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <ModalShell title="Sign timesheet" onClose={onClose}>
      <Field label="Actual start">
        <input
          type="datetime-local"
          value={actualStart}
          onChange={(e) => setActualStart(e.target.value)}
          className="w-full px-2 py-1 border border-ink-soft rounded"
        />
      </Field>
      <Field label="Actual end">
        <input
          type="datetime-local"
          value={actualEnd}
          onChange={(e) => setActualEnd(e.target.value)}
          className="w-full px-2 py-1 border border-ink-soft rounded"
        />
      </Field>
      <Field label="Break (minutes)">
        <input
          type="number"
          min={0}
          max={720}
          value={breakMinutes}
          onChange={(e) => setBreakOverride(Number(e.target.value))}
          className="w-full px-2 py-1 border border-ink-soft rounded"
        />
        <p className="mt-1 font-body text-xs text-ink-muted">
          Defaults to {DEFAULT_BREAK_MINUTES} minutes for shifts over {DEFAULT_BREAK_THRESHOLD_HOURS} hours.
          Change it if the actual break was different.
        </p>
      </Field>
      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-2 py-1 border border-ink-soft rounded"
        />
      </Field>
      {mut.isError && <Alert tone="danger" title="Couldn't save">{messageOf(mut.error)}</Alert>}
      <button
        type="button"
        disabled={mut.isPending}
        onClick={() => mut.mutate()}
        className="mt-2 px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
      >
        Sign + complete shift
      </button>
    </ModalShell>
  );
}

function RateCaregiverModal({
  urlBase,
  shiftId,
  caregiverName,
  onClose,
  onDone,
}: {
  urlBase: string;
  shiftId: string;
  caregiverName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      unwrap(
        // Facility + homecare share this body; type-check against the facility path.
        api.POST(`${urlBase}/{id}/rate-caregiver` as '/api/v1/me/facility/shifts/{id}/rate-caregiver', {
          params: { path: { id: shiftId } },
          body: { ratingStars: stars, comment: comment || undefined },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <ModalShell title={`Rate ${caregiverName}`} onClose={onClose}>
      {/* Audit H16 (2026-09-01) — accessible rating: a radiogroup with named
          radios + aria-checked (the semantic value is `stars`), a filled-vs-
          outline glyph so the selection reads without colour (WCAG 1.4.1), and a
          visible count. Previously five identical unlabeled ★ buttons whose only
          state cue was background colour (WCAG 4.1.2). */}
      <div className="flex items-center gap-2 mb-3" role="radiogroup" aria-label="Overall rating, 1 to 5 stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={n === stars}
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            onClick={() => setStars(n)}
            className={
              'w-8 h-8 rounded-md text-lg ' +
              (n <= stars ? 'bg-sun text-ink' : 'bg-ink-soft text-ink-muted')
            }
          >
            {n <= stars ? '★' : '☆'}
          </button>
        ))}
        <span className="ml-1 text-sm text-ink-muted" aria-hidden="true">
          {stars} / 5
        </span>
      </div>
      <Field label="Comment (optional, non-clinical)">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={1000}
          className="w-full px-2 py-1 border border-ink-soft rounded"
        />
      </Field>
      {mut.isError && <Alert tone="danger" title="Couldn't submit">{messageOf(mut.error)}</Alert>}
      <button
        type="button"
        disabled={mut.isPending}
        onClick={() => mut.mutate()}
        className="mt-2 px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
      >
        Submit rating
      </button>
    </ModalShell>
  );
}

/** Backend `BuyerShiftActionWithReasonSchema` requires `reason` to be
 *  `min(10)` (packages/schemas/src/shift/buyer-views.schema.ts). The client gate
 *  mirrors that minimum so a too-short reason is blocked here instead of round-
 *  tripping to a synthesized "API 400 Bad Request" (ApiError.message ignores the
 *  Zod body). Keep this in sync if the schema minimum changes. */
const MIN_REASON_LENGTH = 10;

/** Shared reason-capture modal for the buyer's cancel + report-no-show actions.
 *  Both hit POST {urlBase}/{id}/{action} with a { reason } body and, on success,
 *  refresh the shift list. Errors (e.g. the too-late / wrong-state guards) render
 *  inline like the other buyer modals. */
export function ReasonActionModal({
  urlBase,
  shiftId,
  action,
  title,
  actionLabel,
  reasonLabel,
  description,
  onClose,
  onDone,
}: {
  urlBase: string;
  shiftId: string;
  action: 'cancel' | 'report-no-show';
  title: string;
  /** Optional consequence text shown above the reason field. */
  description?: string;
  actionLabel: string;
  reasonLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const trimmedLength = reason.trim().length;
  const reasonTooShort = trimmedLength < MIN_REASON_LENGTH;
  const mut = useMutation({
    mutationFn: () =>
      unwrap(
        // Facility + homecare share this body; type-check against the facility path.
        api.POST(`${urlBase}/{id}/${action}` as '/api/v1/me/facility/shifts/{id}/cancel', {
          params: { path: { id: shiftId } },
          body: { reason },
        }),
      ),
    onSuccess: onDone,
  });
  return (
    <ModalShell title={title} onClose={onClose}>
      {description && <p className="font-body text-sm text-ink-muted">{description}</p>}
      <Field label={reasonLabel}>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full px-2 py-1 border border-ink-soft rounded"
          data-testid="reason-action-textarea"
          aria-invalid={reasonTooShort}
        />
      </Field>
      <p className="font-body text-xs text-ink-muted" data-testid="reason-action-helper">
        Please give a reason of at least {MIN_REASON_LENGTH} characters.
      </p>
      {mut.isError && (
        <Alert tone="danger" title="Couldn't submit">
          {messageOf(mut.error)}
        </Alert>
      )}
      <button
        type="button"
        disabled={mut.isPending || reasonTooShort}
        onClick={() => mut.mutate()}
        data-testid="reason-action-submit"
        className="mt-2 px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
      >
        {actionLabel}
      </button>
    </ModalShell>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  // verification audit — this shell backs Sign Timesheet / Rate / Cancel / Report
  // No-Show but was a bare div: no dialog semantics, no focus trap, unlabelled ×.
  // The 2026-07-19 a11y sweep fixed every OTHER portal modal but missed this one.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="buyer-modal-title"
      className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4"
    >
      <div ref={panelRef} className="bg-paper rounded-md p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 id="buyer-modal-title" className="font-heading text-lg text-forest">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-muted text-xl"
          >
            ×
          </button>
        </div>
        <div className="flex flex-col gap-3">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-body text-xs text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function fmtRange(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const dayFmt = new Intl.DateTimeFormat('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat('en-CA', { hour: '2-digit', minute: '2-digit' });
  // Overnight shifts end on a later calendar day than they start; without a
  // marker the end time reads earlier than the start (e.g. "10:00 p.m. – 02:00
  // a.m."), which looks like bad data. Mirror the post form's next-day logic.
  const spansNextDay = s.toDateString() !== e.toDateString();
  return `${dayFmt.format(s)} · ${timeFmt.format(s)} – ${timeFmt.format(e)}${spansNextDay ? ' (next day)' : ''}`;
}

function toLocal(iso: string): string {
  // Convert ISO UTC string to <input type="datetime-local"> format (no Z)
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
